import { getClassificationDataByAdCenterIds } from 'src/app/utils/classifications';
import { isProd } from 'src/app/utils/environment';
import { log } from 'src/app/utils/logging';
import {
  type AdProductUpgrade,
  type DraftJob,
  type DraftJobGuidedDetails,
  GuidedDetailsType,
  type Maybe,
  type DraftJobGuidedDetailsInput,
  type DraftJobProductsInput,
  type ProductSet,
} from 'src/graphql/generated';
import type { FormData, GuidedItem, Session } from 'src/types/appState';
import { guidedDetailsTypeUIToGraphQL } from 'src/types/serializer';

import { verifyArray } from './verifyArray';
import verifyString from './verifyString';

type GuidedDetailsReturnType = {
  [GuidedDetailsType.ABOUT_BUSINESS]: string;
  [GuidedDetailsType.BENEFITS]: string[];
  [GuidedDetailsType.QUALIFICATIONS]: string[];
  [GuidedDetailsType.RESPONSIBILITIES]: string[];
};

export const getGuidedDetail = <K extends GuidedDetailsType>({
  guidedDetails,
  type,
}: {
  guidedDetails?: Maybe<Maybe<DraftJobGuidedDetails>[]>;
  type: K;
}): GuidedDetailsReturnType[K] => {
  const guidedDetail = guidedDetails?.filter((d) => d?.type === type);
  const values = guidedDetail?.[0]?.values;

  if (type === GuidedDetailsType.ABOUT_BUSINESS) {
    const aboutBusinessResult: string = (values?.[0] as string) || '';

    return aboutBusinessResult as GuidedDetailsReturnType[K];
  }

  const result: string[] = (guidedDetail?.[0]?.values as string[]) || [];

  return result as GuidedDetailsReturnType[K];
};

// TODO duplicated logic in graph
// src/schema/draft-job/express-api/utils.ts
export const getQuestionnaireUrlById = (questionnaireId: string): string => {
  const domain = isProd()
    ? 'https://rr-api-s2sauth.cloud.seek.com.au'
    : 'https://rr-api-s2sauth.staging.cloud.seek.com.au';

  return `${domain}/questionnaires/${questionnaireId}`;
};

const filterValues = (guidedDetails: GuidedItem[]): string[] | [] =>
  guidedDetails.map((detail) => detail.value).filter((detail) => detail);

export const mapGuidedDetails = (formData: FormData) => {
  const result: DraftJobGuidedDetailsInput[] = [];
  const { aboutBusiness, benefits, qualifications, responsibilities } =
    formData;
  const guidedDetails: Record<string, GuidedItem[] | undefined> = {
    benefits,
    qualifications,
    responsibilities,
  };

  if (verifyString(aboutBusiness)) {
    result.push({
      type: guidedDetailsTypeUIToGraphQL.aboutBusiness,
      values: [formData.aboutBusiness],
    });
  }

  Object.keys(guidedDetails).forEach((type: string) => {
    const details = guidedDetails[type] || [];
    const values = filterValues(details);

    if (verifyArray(details) && verifyArray(values)) {
      result.push({
        type: guidedDetailsTypeUIToGraphQL[type],
        values,
      });
    }
  });

  return result;
};

export const mapKeySellingPoints = (formData: FormData) => {
  const result: string[] = [];
  const { keySellingPoint1, keySellingPoint2, keySellingPoint3 } = formData;

  if (verifyString(keySellingPoint1)) {
    result.push(keySellingPoint1);
  }

  if (verifyString(keySellingPoint2)) {
    result.push(keySellingPoint2);
  }

  if (verifyString(keySellingPoint3)) {
    result.push(keySellingPoint3);
  }

  return result;
};

export type MapProductsOverrides = Pick<
  GetProductIdProps,
  'productSet' | 'selectedProductId'
>;

export const mapProducts = (
  formData: FormData,
  session: Session,
  overrides?: MapProductsOverrides,
): DraftJobProductsInput => {
  const result: DraftJobProductsInput = {};
  const productSet = overrides?.productSet ?? session.productSet;
  const productIds = getProductIds({
    flowType: formData.flowType,
    isBuyUpsell: session.buyUpSell,
    selectedProductId:
      verifyString(overrides?.selectedProductId) ??
      verifyString(formData.selectedProductId),
    productSet,
  });

  if (productIds.length > 0) {
    result.productIds = Array.from(new Set(productIds));
  }

  if (verifyString(productSet?.id)) {
    result.productSetId = productSet!.id;
  }

  return result;
};

interface GetProductIdProps {
  flowType: string;
  isBuyUpsell: boolean;
  selectedProductId?: string | null;
  productSet?: ProductSet;
}

export const getProductIds = (props: GetProductIdProps): string[] => {
  const productIds: string[] = [];

  if (props.selectedProductId) {
    productIds.push(props.selectedProductId); // Always be Job Ad Edit in Edit flow
  }

  if (props.flowType === 'edit') {
    if (props.isBuyUpsell && props.productSet) {
      const upgradeItems = (
        props.productSet?.items as Array<AdProductUpgrade>
      )?.filter(
        (item) =>
          item &&
          item.features.some((feature) => feature?.type !== 'JobAdEdit'),
      );
      if (upgradeItems.length > 0) {
        productIds.push(upgradeItems[0].id);
      }
      if (upgradeItems.length > 1) {
        log.warn(
          'useDraft | getProductIds | Items Multiple Ad Product Upgrade Items found',
        );
      }
    }
  }
  return productIds;
};

// NOTE:
// New draft api only returns seek categoryId and subCategoryId
// Express api only returns adcentre categoryId and subCategoryId
// Thus we need to ensure if we only have adcentre ids that we convert to seek ids
// Once we move off the old express API then we can remove this below handling of adcentreIds
export const getCategoryIds = (draft: DraftJob) => {
  const {
    adcentreCategoryId,
    adcentreSubCategoryId,
    categoryId,
    subCategoryId,
  } = draft?.categories?.[0] || {};
  if (categoryId && subCategoryId) {
    return { categoryId, subCategoryId };
  }

  if (adcentreCategoryId && adcentreSubCategoryId) {
    const classification = getClassificationDataByAdCenterIds(
      Number(adcentreCategoryId),
      Number(adcentreSubCategoryId),
    );
    return {
      categoryId: classification?.seekClassificationId,
      subCategoryId: classification?.seekSubClassificationId,
    };
  }
  return {};
};
